import { useEffect, useState } from 'react';

/**
 * Copied from spotify-internal/encore-web -- Should replace with encore-web's version
 * once it is properly exported
 */
type SelectedItems = {
  [key: number]: boolean | undefined;
};

const UNFOCUSED_INDEX = -1;
export function useA11yMultiDropdown({
  dropdownListId,
  triggerId,
  itemsLength,
  initialShow = false,
  initialSelected = [],
  onSelected = () => {},
}: {
  dropdownListId: string;
  triggerId: string;
  itemsLength: number;
  initialShow?: boolean;
  initialSelected?: SelectedItems;
  onSelected?: (selected: SelectedItems) => void;
}) {
  const [show, setShowInteral] = useState(initialShow);
  const [selected, setSelected] = useState(initialSelected);
  const [focusedIndex, setFocusedIndex] = useState(UNFOCUSED_INDEX);
  const setShow = (value: boolean) => {
    setShowInteral(value);
    if (!value) {
      setFocusedIndex(UNFOCUSED_INDEX);
    }
  };
  const toggleSelectedIndex = (index: number) => {
    const newValues = {
      ...selected,
      [index]: !selected[index],
    };
    setSelected(newValues);
    onSelected(newValues);
  };

  const onShow = () => {
    const element = document.getElementById(dropdownListId) as HTMLElement;
    if (element) element.focus();
  };

  useEffect(() => {
    if (show) {
      const element = document.getElementById(dropdownListId) as HTMLElement;
      if (element) element.focus();
    }
  }, [dropdownListId, show]);

  const onClick = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    toggleSelectedIndex(index);
  };

  const onHide = () => {
    setShow(false);
    const element = document.getElementById(triggerId) as HTMLElement;
    element.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const upArrow = 38;
    const downArrow = 40;
    const enter = 13;
    const escape = 27;
    const tab = 9;
    const space = 32;

    switch (e.which) {
      case upArrow: {
        if (show) {
          const newIndex = Math.max(UNFOCUSED_INDEX, focusedIndex - 1);
          setFocusedIndex(newIndex);

          if (newIndex < 0) {
            onHide();
          }
        }
        break;
      }

      case downArrow: {
        if (show) {
          const newIndex = Math.min(itemsLength - 1, focusedIndex + 1);
          setFocusedIndex(newIndex);
        } else {
          setShow(true);
        }
        break;
      }

      case enter:
      case space: {
        if (show && focusedIndex > UNFOCUSED_INDEX) {
          toggleSelectedIndex(focusedIndex);
        }
        break;
      }

      case escape:
      case tab: {
        e.preventDefault();
        onHide();
        break;
      }

      default: {
        break;
      }
    }
  };

  return {
    show,
    setShow,
    selected,
    setSelected: (newSelected: SelectedItems) => {
      setSelected(newSelected);
      onSelected(newSelected);
    },
    toggleSelectedIndex,
    focusedIndex,
    setFocusedIndex,
    onClick,
    onShow,
    onHide,
    onKeyDown,
  };
}

export function useA11yDropdown({
  dropdownListId,
  triggerId,
  itemsLength,
  initialShow = false,
  initialSelected = 0,
}: {
  dropdownListId: string;
  triggerId: string;
  itemsLength: number;
  initialShow?: boolean;
  initialSelected?: number;
}) {
  const [show, setShow] = useState(initialShow);
  const [selected, setSelected] = useState(initialSelected);

  useEffect(() => {
    if (show) {
      const element = document.getElementById(dropdownListId) as HTMLElement;
      element.focus();
    }
  });

  const onClick = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    setShow(!show);
    setSelected(index);
  };

  const onHide = () => {
    setShow(false);
    const element = document.getElementById(triggerId) as HTMLElement;
    element.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const upArrow = 38;
    const downArrow = 40;
    const enter = 13;
    const escape = 27;
    const tab = 9;

    switch (e.which) {
      case upArrow: {
        if (selected !== 0) {
          setSelected(selected - 1);
        }
        break;
      }

      case downArrow: {
        if (selected < itemsLength - 1) {
          setSelected(selected + 1);
        }
        break;
      }

      case enter:
      case escape:
      case tab: {
        e.preventDefault();
        setShow(false);
        const element = document.getElementById(triggerId) as HTMLElement;
        element.focus();
        break;
      }

      default: {
        break;
      }
    }
  };

  return { show, setShow, selected, setSelected, onClick, onHide, onKeyDown };
}
